import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, ArrowLeft, CheckCircle, KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage({ onLogin }) {
  const [searchParams] = useSearchParams();
  const defaultMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  
  const [activeTab, setActiveTab] = useState(defaultMode);
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    company_name: '',
    contact_name: '',
    phone: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowResendOption(false);
    setLoginError(''); // Clear previous error
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      
      // Check if this is an employee login
      if (response.data.is_employee) {
        toast.success(`Welcome back, ${response.data.employee.name}!`);
        onLogin(
          response.data.token, 
          response.data.user, 
          response.data.employee,
          response.data.permissions
        );
      } else {
        toast.success('Welcome back!');
        onLogin(response.data.token, response.data.user);
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Show full error details for debugging
      let detail = '';
      
      if (error.response) {
        // Server responded with error
        detail = `Server Error ${error.response.status}: ${JSON.stringify(error.response.data)}`;
      } else if (error.request) {
        // Request was made but no response received
        detail = `No response from server. Request was sent to: ${API}/auth/login`;
      } else {
        // Error setting up the request
        detail = `Request setup error: ${error.message || 'Unknown error'}`;
      }
      
      setLoginError(detail); // Set error to display on page
      
      // Check if email verification is required
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('verify your email')) {
        setShowResendOption(true);
        setUnverifiedEmail(loginData.email);
        toast.error('Please verify your email before logging in.');
      } else {
        toast.error(detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      
      if (response.data.requires_verification) {
        setRegistrationComplete(true);
        setRegisteredEmail(registerData.email);
        toast.success('Account created! Please check your email to verify.');
      } else {
        toast.success('Account created!');
        onLogin(response.data.token, response.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (email) => {
    setResendLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(email)}`);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResendLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
      setForgotPasswordSent(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      // Still show success to prevent email enumeration
      setForgotPasswordSent(true);
      toast.success('If an account exists, a password reset email has been sent.');
    } finally {
      setLoading(false);
    }
  };

  // Show forgot password form
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                DJ CRM
              </h1>
            </Link>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {forgotPasswordSent ? (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
                <p className="text-gray-600 mb-6">
                  If an account exists for <strong>{forgotPasswordEmail}</strong>, we've sent a password reset link.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  The link will expire in 1 hour. Check your spam folder if you don't see it.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordSent(false);
                    setForgotPasswordEmail('');
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <KeyRound className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Forgot Password?</h2>
                  <p className="text-gray-600">
                    Enter your email and we'll send you a link to reset your password.
                  </p>
                </div>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="text-gray-700">Email Address</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="dj@example.com"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      className="bg-gray-50 border-gray-200 text-gray-900"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-gray-600"
                    onClick={() => setShowForgotPassword(false)}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show verification success screen after registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h1>
            <p className="text-gray-600 mb-6">
              We've sent a verification link to<br />
              <span className="font-medium text-gray-900">{registeredEmail}</span>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Click the link in the email to activate your account. The link will expire in 24 hours.
            </p>
            <div className="space-y-3">
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => handleResendVerification(registeredEmail)}
                disabled={resendLoading}
              >
                {resendLoading ? 'Sending...' : 'Resend Verification Email'}
              </Button>
              <Button 
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setRegistrationComplete(false);
                  setActiveTab('login');
                }}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              DJ CRM
            </h1>
          </Link>
          <p className="text-gray-600">Manage your DJ business like a pro</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100 rounded-lg p-1">
              <TabsTrigger 
                value="login" 
                className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="login-tab"
              >
                Login
              </TabsTrigger>
              <TabsTrigger 
                value="register"
                className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="register-tab"
              >
                Register
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-gray-700">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="dj@example.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="login-password" className="text-gray-700">Password</Label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="login-password-input"
                  />
                </div>
                
                {/* Show resend verification option if email is unverified */}
                {showResendOption && (
                  <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                    <p className="text-sm text-yellow-800 mb-2">
                      Your email hasn't been verified yet.
                    </p>
                    <Button 
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleResendVerification(unverifiedEmail)}
                      disabled={resendLoading}
                      className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
                    >
                      {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                    </Button>
                  </div>
                )}
                
                {loginError && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    <strong>Error:</strong> {loginError}
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-5" data-testid="register-form">
                <div className="space-y-2">
                  <Label htmlFor="register-company" className="text-gray-700">Company Name</Label>
                  <Input
                    id="register-company"
                    placeholder="DJ Pro Entertainment"
                    value={registerData.company_name}
                    onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="register-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-name" className="text-gray-700">Your Name</Label>
                  <Input
                    id="register-name"
                    placeholder="John Doe"
                    value={registerData.contact_name}
                    onChange={(e) => setRegisterData({ ...registerData, contact_name: e.target.value })}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="register-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-email" className="text-gray-700">Email</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="dj@example.com"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="register-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-phone" className="text-gray-700">Phone (Optional)</Label>
                  <Input
                    id="register-phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="register-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-password" className="text-gray-700">Password</Label>
                  <Input
                    id="register-password"
                    type="password"
                    placeholder="••••••••"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    minLength={6}
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="register-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creating Account...' : 'Start 14-Day Free Trial'}
                </Button>
                <p className="text-xs text-center text-gray-500">
                  No credit card required. By signing up, you agree to our Terms of Service.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
        
        <p className="text-center mt-6 text-sm text-gray-500">
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            &larr; Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
